import React from 'react';
import { Card, Input, Select } from 'antd';
import { DestinationAreaEditor } from 'components/DestinationMediaEditor/DestinationAreaEditor';
import FormField from 'components/FormField';
import {
    DESTINATION_DESCRIPTION_MAX_LENGTH,
    DESTINATION_TAGLINE_MAX_LENGTH,
} from 'constants/fields-limits';
import { DestinationContentTypes } from 'constants/destination-content-types';
import { DestinationStatuses } from 'constants/destination-statuses';
import { DestinationMapVisibility } from 'constants/destination-map-visibility';
import { DestinationPopularity } from 'constants/destination-popularity';
import AuthorSelector from 'components/AuthorSelector';
import {
    mapVisibilityOptions,
    popularityOptions,
} from './use-destination-basic-info-form';
import { getValidatedPinLocation } from 'utils/validated-pin-location';
import { Destination } from './DestinationEditorContext';
import styles from './DestinationEditorBasicInfo.module.css';

import type { FieldErrors } from './use-destination-basic-info-form';

const { TextArea } = Input;

export type DestinationBasicInfoCommonFieldsProps = Readonly<{
    fieldErrors: FieldErrors;
    taglineContent: string;
    setTaglineContent: (value: string) => void;
    descriptionContent: string;
    setDescriptionContent: (value: string) => void;
    destination: Destination;
    handleUpdateTaglineOrDescription: (
        body: string,
        type:
            | typeof DestinationContentTypes.TAGLINE
            | typeof DestinationContentTypes.DESCRIPTION,
    ) => void;
    handleUpdateField: <T extends keyof Destination>(
        field: T,
        value: Destination[T],
    ) => void;
    handleUpdateEditor: (newEditorId: Id, newEditor: CMSUser) => void;
    showPopularity?: boolean;
}>;

export function DestinationBasicInfoCommonFields({
    fieldErrors,
    taglineContent,
    setTaglineContent,
    descriptionContent,
    setDescriptionContent,
    destination,
    handleUpdateTaglineOrDescription,
    handleUpdateField,
    handleUpdateEditor,
    showPopularity = false,
}: DestinationBasicInfoCommonFieldsProps) {
    return (
        <>
            <FormField
                label="Tagline"
                error={fieldErrors.tagline}
                length={taglineContent.length}
                maxLength={DESTINATION_TAGLINE_MAX_LENGTH}
            >
                <Input
                    status={fieldErrors.tagline ? 'error' : undefined}
                    value={taglineContent}
                    onChange={(e) => setTaglineContent(e.target.value)}
                    onBlur={(e) =>
                        handleUpdateTaglineOrDescription(
                            e.target.value,
                            DestinationContentTypes.TAGLINE,
                        )
                    }
                    size="large"
                    placeholder="Optional"
                />
            </FormField>
            <FormField
                label="Description"
                length={descriptionContent.length}
                maxLength={DESTINATION_DESCRIPTION_MAX_LENGTH}
            >
                <TextArea
                    className="form-field__textarea"
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    value={descriptionContent}
                    onChange={(e) => setDescriptionContent(e.target.value)}
                    onBlur={(e) => {
                        handleUpdateTaglineOrDescription(
                            e.target.value,
                            DestinationContentTypes.DESCRIPTION,
                        );
                    }}
                    size="large"
                    placeholder="Optional"
                />
            </FormField>
            <FormField label="Status">
                <Select
                    size="large"
                    defaultValue={DestinationStatuses.DRAFT}
                    value={destination.status}
                    onChange={(value) => handleUpdateField('status', value)}
                    className={styles.statusSelect}
                >
                    <Select.Option value={DestinationStatuses.DRAFT}>
                        Draft
                    </Select.Option>
                    <Select.Option value={DestinationStatuses.PUBLISHED}>
                        Published
                    </Select.Option>
                    <Select.Option value={DestinationStatuses.ARCHIVED}>
                        Archived
                    </Select.Option>
                </Select>
            </FormField>
            <FormField label="Editor">
                <AuthorSelector
                    value={destination.main_editor_id || ''}
                    onChange={handleUpdateEditor}
                />
            </FormField>
            <FormField label="Map Visibility">
                <Select
                    size="large"
                    defaultValue={DestinationMapVisibility.HIGHEST_ZOOM}
                    value={destination.map_visibility}
                    onChange={(value) =>
                        handleUpdateField('map_visibility', value)
                    }
                    className={styles.statusSelect}
                >
                    {mapVisibilityOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                            {option.label}
                        </Select.Option>
                    ))}
                </Select>
            </FormField>
            {showPopularity && (
                <FormField label="Popularity">
                    <Select
                        size="large"
                        defaultValue={DestinationPopularity.RARELY_EXPLORED}
                        value={destination.popularity}
                        onChange={(value) =>
                            handleUpdateField('popularity', value)
                        }
                        className={styles.statusSelect}
                    >
                        {popularityOptions.map((option) => (
                            <Select.Option
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </FormField>
            )}

            <Card title="Map" className={styles.mapCard}>
                <DestinationAreaEditor
                    value={
                        destination.boundary_area
                            ? JSON.parse(destination.boundary_area)
                            : null
                    }
                    onChange={(newArea) => {
                        handleUpdateField(
                            'boundary_area',
                            JSON.stringify(newArea),
                        );
                    }}
                    pinLocation={getValidatedPinLocation(destination)}
                    onPinLocationChange={(newLocation) => {
                        handleUpdateField('lat', newLocation.lat);
                        handleUpdateField('lon', newLocation.lon);
                    }}
                />
            </Card>
        </>
    );
}
